import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Trash2, 
  Edit, 
  Save, 
  X, 
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Circle,
  AlertCircle,
  ArrowLeft,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import examenService from '../../services/examenService';
import preguntaService from '../../services/preguntaService';
import opcionService from '../../services/opcionService';
import useAuthStore from '../../store/authStore';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Loader from '../../components/ui/Loader';
import AuthImage from '../../components/common/AuthImage';
import ImageViewer from '../../components/common/ImageViewer';
import PreguntaModal from '../../components/exam/PreguntaModal';
import OpcionModal from '../../components/exam/OpcionModal';

const GestionarExamen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [examen, setExamen] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  
  // Base URL para las imágenes
  const BASE_IMAGE_URL = 'http://172.17.249.98:9099';
  const [preguntaExpandida, setPreguntaExpandida] = useState(null);
  const [preguntaActualIndex, setPreguntaActualIndex] = useState(0);
  
  // Estados para modales
  const [showPreguntaModal, setShowPreguntaModal] = useState(false);
  const [preguntaToEdit, setPreguntaToEdit] = useState(null);
  
  const [showOpcionModal, setShowOpcionModal] = useState(false);
  const [opcionToEdit, setOpcionToEdit] = useState(null);
  const [preguntaIdForOpcion, setPreguntaIdForOpcion] = useState(null);

  useEffect(() => {
    loadExamen();
  }, [id]);

  const loadExamen = async () => {
    try {
      setLoading(true);
      const examenData = await examenService.getExamenById(id);
      console.log('🔍 Datos del examen cargado:', examenData);
      console.log('🔍 Tipo de examen:', examenData.tipoExamen);
      setExamen(examenData);
      
      // Cargar preguntas del examen
      const preguntasData = await preguntaService.getAllPreguntas();
      const preguntasDelExamen = (preguntasData || []).filter(
        p => p.examenId === parseInt(id) || p.examen?.id === parseInt(id)
      );
      console.log('🔍 Preguntas encontradas:', preguntasDelExamen.length);
      setPreguntas(preguntasDelExamen);
    } catch (error) {
      console.error('Error al cargar examen:', error);
      toast.error('Error al cargar el examen');
    } finally {
      setLoading(false);
    }
  };

  const crearExamenRavenAutomatico = async () => {
    if (!window.confirm('¿Crear automáticamente el Examen Raven Oficial con 12 preguntas y 96 opciones?')) {
      return;
    }

    const toastId = toast.loading('Creando examen Raven...');
    
    try {
      setLoading(true);

      // Respuestas correctas
      const respuestasCorrectas = {
        1: 1, 2: 7, 3: 8, 4: 5, 5: 5, 6: 7,
        7: 6, 8: 8, 9: 1, 10: 1, 11: 6, 12: 3
      };

      // Crear 12 preguntas (1 por 1)
      for (let numPregunta = 1; numPregunta <= 12; numPregunta++) {
        toast.loading(`Creando pregunta ${numPregunta}/12...`, { id: toastId });

        // Intentar cargar la imagen de la pregunta
        let blobPregunta = null;
        try {
          const responsePregunta = await fetch(`/raven preguntas - opciones/EXAMEN OFICIAL/${numPregunta}.png`);
          if (responsePregunta.ok) {
            blobPregunta = await responsePregunta.blob();
          }
        } catch (error) {
          console.warn(`No se pudo cargar imagen de pregunta ${numPregunta}:`, error);
        }

        // Crear FormData con el formato correcto: campo "pregunta" como JSON blob
        const formDataPregunta = new FormData();
        
        // Objeto pregunta como JSON
        const preguntaObj = {
          texto: `Pregunta ${numPregunta}`,
          tipo: 'opcion_multiple',
          peso: 10,
          examenId: parseInt(id),
          opciones: []
        };
        
        // Agregar el objeto pregunta como blob JSON
        const preguntaBlob = new Blob([JSON.stringify(preguntaObj)], { type: 'application/json' });
        formDataPregunta.append('pregunta', preguntaBlob);
        
        // Agregar la imagen si existe
        if (blobPregunta) {
          formDataPregunta.append('imagen', blobPregunta, `${numPregunta}.PNG`);
        }

        // Crear la pregunta
        const preguntaCreada = await preguntaService.createPreguntaWithImage(formDataPregunta);
        console.log(`✅ Pregunta ${numPregunta} creada con ID:`, preguntaCreada.id);

        // Crear 8 opciones para esta pregunta (1 por 1)
        for (let numOpcion = 1; numOpcion <= 8; numOpcion++) {
          const esCorrecta = respuestasCorrectas[numPregunta] === numOpcion;

          toast.loading(`Creando pregunta ${numPregunta}/12 - Opción ${numOpcion}/8...`, { id: toastId });

          // Intentar cargar la imagen de la opción
          let blobOpcion = null;
          try {
            const responseOpcion = await fetch(`/raven preguntas - opciones/EXAMEN OFICIAL/${numPregunta}.${numOpcion}.png`);
            if (responseOpcion.ok) {
              blobOpcion = await responseOpcion.blob();
            }
          } catch (error) {
            console.warn(`No se pudo cargar imagen de opción ${numPregunta}.${numOpcion}:`, error);
          }

          // Crear FormData con el formato correcto: campo "opcion" como JSON blob
          const formDataOpcion = new FormData();
          
          // Objeto opción como JSON
          const opcionObj = {
            texto: `Opción ${numOpcion}`,
            preguntaId: preguntaCreada.id,
            esCorrecta: esCorrecta
          };
          
          // Agregar el objeto opción como blob JSON
          const opcionBlob = new Blob([JSON.stringify(opcionObj)], { type: 'application/json' });
          formDataOpcion.append('opcion', opcionBlob);
          
          // Agregar la imagen si existe
          if (blobOpcion) {
            formDataOpcion.append('imagen', blobOpcion, `${numPregunta}.${numOpcion}.PNG`);
          }

          // Crear la opción
          await opcionService.createOpcionWithImage(formDataOpcion);
          console.log(`  ✅ Opción ${numPregunta}.${numOpcion} creada ${esCorrecta ? '(CORRECTA)' : ''}`);
        }
      }

      toast.success('¡Examen Raven creado exitosamente! 12 preguntas y 96 opciones', { id: toastId });
      await loadExamen();
    } catch (error) {
      console.error('❌ Error creando examen automático:', error);
      toast.error('Error al crear el examen automático: ' + error.message, { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  // PREGUNTA - Crear/Actualizar
  const handleSavePregunta = async (data, preguntaId, hasImage) => {
    try {
      if (preguntaId) {
        // Actualizar pregunta existente
        if (hasImage) {
          // Si tiene imagen nueva, usar endpoint con FormData
          await preguntaService.updatePreguntaWithImage(preguntaId, data);
        } else {
          // Si NO tiene imagen nueva, usar endpoint normal con JSON
          await preguntaService.updatePregunta(preguntaId, data);
        }
        toast.success('Pregunta actualizada exitosamente');
      } else {
        // Crear nueva pregunta
        if (hasImage) {
          await preguntaService.createPreguntaWithImage(data);
        } else {
          await preguntaService.createPregunta(data);
        }
        toast.success('Pregunta creada exitosamente');
      }
      setShowPreguntaModal(false);
      setPreguntaToEdit(null);
      loadExamen();
    } catch (error) {
      console.error('Error al guardar pregunta:', error);
      toast.error('Error al guardar la pregunta');
      throw error; // Re-throw para que el modal maneje el error
    }
  };

  // PREGUNTA - Eliminar
  const handleEliminarPregunta = async (preguntaId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta pregunta y todas sus opciones?')) {
      return;
    }

    try {
      await preguntaService.deletePregunta(preguntaId);
      toast.success('Pregunta eliminada');
      loadExamen();
    } catch (error) {
      console.error('Error al eliminar pregunta:', error);
      toast.error('Error al eliminar pregunta');
    }
  };

  // OPCIÓN - Crear/Actualizar
  const handleSaveOpcion = async (data, opcionId, preguntaId, hasImage) => {
    console.log('🔍 handleSaveOpcion:', {
      opcionId,
      preguntaId,
      hasImage,
      dataType: data instanceof FormData ? 'FormData' : 'JSON',
      data: data instanceof FormData ? 'FormData object' : data
    });

    try {
      if (opcionId) {
        // Actualizar opción existente
        if (hasImage) {
          console.log('📡 Llamando updateOpcionWithImage (FormData)');
          await opcionService.updateOpcionWithImage(opcionId, data);
        } else {
          console.log('📡 Llamando updateOpcion (JSON)');
          await opcionService.updateOpcion(opcionId, data);
        }
        toast.success('Opción actualizada exitosamente');
      } else {
        // Crear nueva opción
        if (hasImage) {
          console.log('📡 Llamando createOpcionWithImage (FormData)');
          await opcionService.createOpcionWithImage(data);
        } else {
          console.log('📡 Llamando createOpcion (JSON)');
          await opcionService.createOpcion(data);
        }
        toast.success('Opción creada exitosamente');
      }
      setShowOpcionModal(false);
      setOpcionToEdit(null);
      setPreguntaIdForOpcion(null);
      loadExamen();
    } catch (error) {
      console.error('❌ Error al guardar opción:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Error al guardar la opción');
      throw error; // Re-throw para que el modal maneje el error
    }
  };

  // OPCIÓN - Eliminar
  const handleEliminarOpcion = async (opcionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta opción?')) {
      return;
    }

    try {
      await opcionService.deleteOpcion(opcionId);
      toast.success('Opción eliminada');
      loadExamen();
    } catch (error) {
      console.error('Error al eliminar opción:', error);
      toast.error('Error al eliminar opción');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader size="lg" />
      </div>
    );
  }

  if (!examen) {
    return (
      <Card>
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Examen no encontrado
          </h3>
          <Button variant="primary" onClick={() => navigate(-1)}>
            Volver
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">{examen.nombre}</h1>
            <p className="text-neutral-600 mt-1">{examen.descripcion}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="secondary">
            {preguntas.length} {preguntas.length === 1 ? 'pregunta' : 'preguntas'}
          </Badge>
          <Badge variant="info">
            {examen.tiempoMinutos} minutos
          </Badge>
        </div>
      </div>

      {/* Información del examen */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-neutral-600 mb-1">Perfil</p>
            <p className="font-medium text-neutral-900">
              {examen.perfilNombre || examen.perfil?.nombre || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Duración</p>
            <p className="font-medium text-neutral-900">{examen.tiempoMinutos} minutos</p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Ponderación</p>
            <p className="font-medium text-neutral-900">{examen.porcentajePonderacion}%</p>
          </div>
        </div>
      </Card>

      {/* Lista de preguntas */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-neutral-900">Preguntas y Opciones</h2>
          <div className="flex gap-2">
            {preguntas.length === 0 && (
              <>
                {/* Debug info */}
                <div className="text-xs text-neutral-500 flex items-center gap-2 mr-2">
                  Tipo: <Badge variant="info">{examen.tipoExamen || 'N/A'}</Badge>
                </div>
                
                {/* Botón Raven - Mostrar si es tipo RAVEN o si el nombre contiene "raven" (case insensitive) */}
                {(examen.tipoExamen === 'RAVEN' || 
                  examen.nombre?.toLowerCase().includes('raven') ||
                  examen.descripcion?.toLowerCase().includes('raven')) && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={crearExamenRavenAutomatico}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700"
                  >
                    <Upload className="w-4 h-4" />
                    Crear Examen Raven Automático (12 preguntas)
                  </Button>
                )}
              </>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowPreguntaModal(true)}
            >
              <Plus className="w-4 h-4" />
              Nueva Pregunta
            </Button>
          </div>
        </div>

        {/* Lista de preguntas */}
        {preguntas.length === 0 ? (
          <div className="text-center py-12 bg-neutral-50 rounded-lg">
            <AlertCircle className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
            <p className="text-neutral-600">
              No hay preguntas en este examen. Crea una para comenzar.
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Navegación del carrusel */}
            <div className="flex items-center justify-between mb-4 px-2">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-neutral-700 bg-neutral-100 px-3 py-1.5 rounded-full">
                  Pregunta {preguntaActualIndex + 1} de {preguntas.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreguntaActualIndex(Math.max(0, preguntaActualIndex - 1))}
                  disabled={preguntaActualIndex === 0}
                  className="p-2 rounded-lg border border-neutral-300 hover:bg-primary hover:text-white hover:border-primary disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-neutral-700 transition-all"
                  title="Pregunta anterior"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPreguntaActualIndex(Math.min(preguntas.length - 1, preguntaActualIndex + 1))}
                  disabled={preguntaActualIndex === preguntas.length - 1}
                  className="p-2 rounded-lg border border-neutral-300 hover:bg-primary hover:text-white hover:border-primary disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-neutral-700 transition-all"
                  title="Pregunta siguiente"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Indicadores de puntos */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {preguntas.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setPreguntaActualIndex(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === preguntaActualIndex
                      ? 'w-10 h-2.5 bg-primary shadow-md'
                      : 'w-2.5 h-2.5 bg-neutral-300 hover:bg-neutral-400 hover:w-4'
                  }`}
                  title={`Ir a pregunta ${index + 1}`}
                />
              ))}
            </div>

            {/* Carrusel de preguntas */}
            <AnimatePresence mode="wait">
              {preguntas.map((pregunta, index) => index === preguntaActualIndex && (
                <motion.div
                  key={pregunta.id}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="border border-neutral-200 rounded-lg overflow-hidden shadow-sm"
                >
                {/* Header de la pregunta */}
                <div className="p-4 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-bold text-sm">#{index + 1}</span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="flex-1">
                              <h3 className="font-semibold text-neutral-900 text-base mb-2">
                                {pregunta.texto}
                              </h3>
                              <div className="flex items-center gap-3 flex-wrap">
                                <Badge variant={pregunta.tipo === 'verdadero_falso' ? 'secondary' : 'info'}>
                                  {pregunta.tipo === 'verdadero_falso' ? 'Verdadero/Falso' : 'Opción Múltiple'}
                                </Badge>
                                <span className="text-sm text-neutral-600 flex items-center gap-1">
                                  <span className="font-medium">Peso:</span> {pregunta.peso} pts
                                </span>
                                <span className="text-sm text-neutral-600 flex items-center gap-1">
                                  <span className="font-medium">Opciones:</span> {pregunta.opciones?.length || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                          {(pregunta.urlImagen || pregunta.imagen) && (
                            <div className="mt-4 flex justify-center">
                              <ImageViewer 
                                src={pregunta.imagen || pregunta.urlImagen} 
                                alt="Imagen de la pregunta" 
                                className="w-full h-64 object-contain rounded-xl border-2 border-neutral-200 shadow-lg bg-neutral-50"
                                containerClassName="w-full max-w-2xl"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setPreguntaToEdit(pregunta);
                          setShowPreguntaModal(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleEliminarPregunta(pregunta.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPreguntaExpandida(preguntaExpandida === pregunta.id ? null : pregunta.id)}
                      >
                        {preguntaExpandida === pregunta.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Opciones de la pregunta (expandible) */}
                <AnimatePresence>
                  {preguntaExpandida === pregunta.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="border-t border-neutral-200 bg-neutral-50 p-4"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-neutral-900">Opciones de Respuesta</h4>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setPreguntaIdForOpcion(pregunta.id);
                            setShowOpcionModal(true);
                          }}
                        >
                          <Plus className="w-4 h-4" />
                          Nueva Opción
                        </Button>
                      </div>

                      {/* Lista de opciones */}
                      {pregunta.opciones && pregunta.opciones.length > 0 ? (
                        <div className="space-y-2">
                          {pregunta.opciones.map((opcion) => (
                            <div
                              key={opcion.id}
                              className="p-3 bg-white rounded-lg border border-neutral-200"
                            >
                              <div className={`group hover:bg-white/50 rounded-lg p-3 transition-all ${
                                opcion.esCorrecta ? 'bg-green-50/50 border-l-4 border-green-500' : 'bg-white border-l-4 border-neutral-200'
                              }`}>
                                <div className="flex items-start gap-4">
                                  <div className="flex-shrink-0 mt-1">
                                    {opcion.esCorrecta ? (
                                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                      </div>
                                    ) : (
                                      <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                                        <Circle className="w-5 h-5 text-neutral-400" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="text-neutral-900 font-medium">{opcion.texto}</span>
                                      {opcion.esCorrecta && (
                                        <Badge variant="success" className="text-xs">✓ Correcta</Badge>
                                      )}
                                    </div>
                                    {opcion.imagen && (
                                      <div className="mt-3 flex justify-center">
                                        <ImageViewer 
                                          src={opcion.imagen} 
                                          alt="Imagen de la opción" 
                                          className="w-full h-40 object-contain rounded-lg border-2 border-neutral-200 shadow-md bg-white"
                                          containerClassName="w-full max-w-sm"
                                        />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setOpcionToEdit(opcion);
                                        setPreguntaIdForOpcion(pregunta.id);
                                        setShowOpcionModal(true);
                                      }}
                                      className="hover:bg-primary hover:text-white hover:border-primary"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleEliminarOpcion(opcion.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 bg-white rounded-lg border border-neutral-200">
                          <AlertCircle className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                          <p className="text-sm text-neutral-600">
                            No hay opciones para esta pregunta
                          </p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </Card>

      {/* Modales */}
      <PreguntaModal
        isOpen={showPreguntaModal}
        onClose={() => {
          setShowPreguntaModal(false);
          setPreguntaToEdit(null);
        }}
        onSave={handleSavePregunta}
        initialData={preguntaToEdit}
        examenId={id}
      />

      <OpcionModal
        isOpen={showOpcionModal}
        onClose={() => {
          setShowOpcionModal(false);
          setOpcionToEdit(null);
          setPreguntaIdForOpcion(null);
        }}
        onSave={handleSaveOpcion}
        initialData={opcionToEdit}
        preguntaId={preguntaIdForOpcion}
      />
    </div>
  );
};

export default GestionarExamen;
